<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-body pb-10 pt-10 page-container">
          <div class="mb-4">
            <h4 class="mb-0">
              {{
                $t(
                  "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.PAGE_MEMBER_DETAIL_TITLE"
                )
              }}
            </h4>
          </div>

          <b-tabs v-model="tabIndex" content-class="mt-3" @input="onTabInput">
            <b-tab
              :title="
                $t('PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TAB_PROFILE')
              "
              lazy
            >
              <campaign-member-profile
                embed
                :campaign-id="campaignId"
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
            <b-tab v-if="isMemberTierEnabled" title="สมุดแต้ม" lazy>
              <campaign-member-point-ledger
                embed
                :campaign-id="campaignId"
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
            <b-tab v-if="isMemberTierEnabled" title="ประวัติระดับสมาชิก" lazy>
              <campaign-member-tier-history
                embed
                :campaign-id="campaignId"
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
            <b-tab
              v-if="isReceiptUploadEnabled"
              :title="
                $t('PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TAB_POINTS')
              "
              lazy
            >
              <campaign-member-point-history
                embed
                :campaign-id="campaignId"
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
            <b-tab
              v-if="isQrScanEnabled"
              :title="
                $t(
                  'PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TAB_QR_REDEMPTION_HISTORY'
                )
              "
              lazy
            >
              <campaign-qr-history
                :campaign-id="campaignId"
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
            <b-tab
              :title="
                $t('PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.TAB_REDEMPTION')
              "
              lazy
            >
              <campaign-member-redemption-history
                embed
                :campaign-id="campaignId"
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
            <b-tab title="แต้มหมดอายุ" lazy>
              <point-expire-member-summary
                :customer-campaign-id="customerCampaignId"
              />
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { FIND_CAMPAIGN } from "@/core/services/store/campaign.module";
import CampaignQrHistory from "@/view/pages/qr-redeem/CampaignQrHistory.vue";
import CampaignMemberProfile from "./CampaignMemberProfile.vue";
import CampaignMemberPointHistory from "./CampaignMemberPointHistory.vue";
import CampaignMemberRedemptionHistory from "./CampaignMemberRedemptionHistory.vue";
import CampaignMemberPointLedger from "./CampaignMemberPointLedger.vue";
import CampaignMemberTierHistory from "./CampaignMemberTierHistory.vue";
import PointExpireMemberSummary from "@/view/pages/point-expire/components/PointExpireMemberSummary.vue";

export default {
  name: "CampaignMemberDetail",
  components: {
    CampaignMemberProfile,
    CampaignMemberPointHistory,
    CampaignMemberRedemptionHistory,
    CampaignMemberPointLedger,
    CampaignMemberTierHistory,
    PointExpireMemberSummary,
    CampaignQrHistory
  },
  data() {
    return {
      tabIndex: 0,
      isReceiptUploadEnabled: true,
      isQrScanEnabled: false
    };
  },
  computed: {
    ...mapGetters(["isMemberTierEnabled"]),
    visibleTabs() {
      const tabs = ["profile"];
      if (this.isMemberTierEnabled) {
        tabs.push("ledger");
        tabs.push("tier-history");
      }
      if (this.isReceiptUploadEnabled) {
        tabs.push("points");
      }
      if (this.isQrScanEnabled) {
        tabs.push("qr-redemption-history");
      }
      tabs.push("redemption");
      tabs.push("point-expire");
      return tabs;
    },
    campaignId() {
      return this.$route.params.campaignId;
    },
    customerCampaignId() {
      return this.$route.params.customerCampaignId;
    }
  },
  watch: {
    "$route.query.tab": {
      handler(val) {
        this.tabIndex = this.tabIndexFromQuery(val);
      }
    }
  },
  mounted() {
    this.tabIndex = this.tabIndexFromQuery(this.$route.query.tab);
    this.loadCampaignSettings();
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: `${this.$t("MENU.CAMPAIGN_MANAGEMENT.CAMPAIGN")}`,
        route: "/campaign"
      },
      {
        title: `${this.$t("PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TITLE")}`,
        route: `/campaign/form/campaign_receipt/${this.campaignId}`
      },
      {
        title: `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.PAGE_MEMBER_DETAIL_TITLE"
        )}`
      }
    ]);
  },
  methods: {
    async loadCampaignSettings() {
      try {
        const { data } = await this.$store.dispatch(FIND_CAMPAIGN, {
          id: this.campaignId
        });
        if (data.status === "success" && data.data) {
          const receiptUploadEnabled = data.data.is_receipt_upload_enabled;
          this.isReceiptUploadEnabled =
            receiptUploadEnabled === undefined || receiptUploadEnabled === null
              ? true
              : receiptUploadEnabled === true ||
                receiptUploadEnabled === 1 ||
                receiptUploadEnabled === "1";
          const qrScanEnabled = data.data.is_qr_scan_enabled;
          this.isQrScanEnabled =
            qrScanEnabled === true || qrScanEnabled === 1 || qrScanEnabled === "1";
        }
      } catch (error) {
        // Keep the existing tabs visible if campaign settings cannot be loaded.
      }

      this.tabIndex = this.tabIndexFromQuery(this.$route.query.tab);
    },
    tabIndexFromQuery(tab) {
      const t = (tab != null ? String(tab) : "profile").toLowerCase();
      const index = this.visibleTabs.indexOf(t);
      return index >= 0 ? index : 0;
    },
    queryNameFromTabIndex(idx) {
      return this.visibleTabs[idx] || "profile";
    },
    onTabInput(idx) {
      const tab = this.queryNameFromTabIndex(idx);
      if (this.$route.query.tab === tab) {
        return;
      }
      this.$router
        .replace({
          query: { ...this.$route.query, tab }
        })
        .catch(() => {});
    }
  }
};
</script>
